import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Alert, AlertIcon, Box, Button, Container, Flex, FormControl, FormHelperText, FormLabel, Heading, HStack,
  Image, Input, Link, SimpleGrid, Switch, Text, Textarea, VStack,
} from '@chakra-ui/react';
import { Helmet } from 'react-helmet-async';
import { FiArrowRight, FiCheckCircle } from 'react-icons/fi';
import { DIRECTORY_SLUG } from './DirectoryPage';
import { track } from '../utils/analytics';

const API = () => process.env.REACT_APP_AWS_API_URL || '';

const EMPTY = {
  name: '', street: '', city: '', state: '', zipCode: '', phone: '', website: '', services: '',
  description: '', offersDelivery: false, deliveryZips: '', contactName: '', contactEmail: '',
};

function SoftwareUpsell({ claimId }) {
  return (
    <Box p={5} borderRadius="xl" bg="blue.50" border="1px solid" borderColor="blue.100">
      <Text fontWeight="700" mb={1}>Also want online ordering?</Text>
      <Text fontSize="sm" color="gray.600" mb={3}>
        Optional. Smart Laundry Basket can also run your pickup &amp; delivery orders, counter and payments.
        Shops on it get an &quot;Order now&quot; button and rank first in the directory.
      </Text>
      <Button as="a" href={claimId ? `/quickstart?claim=${claimId}` : '/quickstart'} size="sm" colorScheme="blue"
        variant="outline" borderRadius="full" rightIcon={<FiArrowRight />}>
        Start free with the software
      </Button>
    </Box>
  );
}

export default function GetListedPage() {
  const claimId = (new URLSearchParams(window.location.search).get('claim') || '').replace(/\D/g, '');
  const [form, setForm] = useState(EMPTY);
  const [claimName, setClaimName] = useState('');
  const [status, setStatus] = useState({ sending: false, done: false, error: '' });

  useEffect(() => {
    if (!claimId) return;
    axios.get(`${API()}/api/directory/outside/${claimId}`)
      .then(({ data }) => {
        setClaimName(data.name);
        setForm((f) => ({
          ...f,
          name: data.name || '', street: data.street || '', city: data.city || '', state: data.state || '',
          zipCode: data.zipCode || '', phone: data.phone || '', website: data.website || '',
          services: (data.services || []).join(', '), description: data.description || '',
          offersDelivery: !!data.offersDelivery,
        }));
      })
      .catch(() => {});
  }, [claimId]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus({ sending: true, done: false, error: '' });
    try {
      await axios.post(`${API()}/api/directory/request`, {
        ...form,
        serviceableZipCodes: form.offersDelivery ? form.deliveryZips.split(/[\s,]+/).filter(Boolean) : [],
        claimListingId: claimName ? claimId : null,
      });
      track('directory_listing_request', { source: claimName ? 'claim' : 'new' });
      setStatus({ sending: false, done: true, error: '' });
      window.scrollTo(0, 0);
    } catch (err) {
      setStatus({ sending: false, done: false, error: err.response?.data?.detail || 'Something went wrong. Please try again.' });
    }
  };

  return (
    <Box bg="gray.50" minH="100vh">
      <Helmet>
        <title>Get your laundromat listed free | Wash &amp; Fold Near Me</title>
        <meta name="description" content="Add your laundromat to the free wash & fold near me directory. No software or contract required." />
        <meta name="robots" content="noindex" />
      </Helmet>

      <Box as="nav" bg="white" borderBottom="1px solid" borderColor="gray.100">
        <Container maxW="900px">
          <Flex py={3} align="center">
            <Link href={`/${DIRECTORY_SLUG}`}><Image src="/slb-logo.png" alt="Smart Laundry Basket" h={{ base: '40px', md: '52px' }} objectFit="contain" /></Link>
          </Flex>
        </Container>
      </Box>

      <Container maxW="760px" py={{ base: 8, md: 12 }}>
        {status.done ? (
          <VStack spacing={6} align="stretch">
            <VStack spacing={3} textAlign="center" bg="white" p={8} borderRadius="xl" boxShadow="sm">
              <Box color="green.500" fontSize="48px"><FiCheckCircle /></Box>
              <Heading fontSize="2xl">Thanks, we got it!</Heading>
              <Text color="gray.600">
                We&apos;ll review {form.name} and email {form.contactEmail} once it&apos;s live, usually within 1–2 business days.
              </Text>
              <Button as="a" href={`/${DIRECTORY_SLUG}`} variant="link" colorScheme="blue">Back to the directory</Button>
            </VStack>
            <SoftwareUpsell claimId={claimName ? claimId : ''} />
          </VStack>
        ) : (
          <VStack spacing={6} align="stretch">
            <Box textAlign="center">
              <Heading as="h1" fontSize={{ base: '2xl', md: '4xl' }} mb={3}>
                {claimName ? `Claim ${claimName}` : 'Get your laundromat listed free'}
              </Heading>
              <Text color="gray.600" fontSize={{ base: 'md', md: 'lg' }}>
                Show up when local customers search for wash &amp; fold near them.
                <b> Free. No software, no contract, no credit card.</b>
              </Text>
            </Box>

            {claimName && (
              <Alert status="info" borderRadius="xl">
                <AlertIcon />Check your details below and fix anything that&apos;s wrong. We&apos;ll update the listing once we confirm it&apos;s yours.
              </Alert>
            )}

            <Box as="form" onSubmit={submit} bg="white" p={{ base: 5, md: 8 }} borderRadius="xl" boxShadow="sm">
              <VStack spacing={5} align="stretch">
                <Heading as="h2" fontSize="lg">Your laundromat</Heading>
                <FormControl isRequired>
                  <FormLabel>Business name</FormLabel>
                  <Input value={form.name} onChange={set('name')} maxLength={255} />
                </FormControl>
                <FormControl>
                  <FormLabel>Street address</FormLabel>
                  <Input value={form.street} onChange={set('street')} maxLength={255} />
                  <FormHelperText>Leave blank if customers can&apos;t drop off at your location.</FormHelperText>
                </FormControl>
                <SimpleGrid columns={{ base: 1, sm: 3 }} spacing={4}>
                  <FormControl isRequired><FormLabel>City</FormLabel><Input value={form.city} onChange={set('city')} maxLength={100} /></FormControl>
                  <FormControl isRequired><FormLabel>State</FormLabel><Input value={form.state} onChange={set('state')} maxLength={50} placeholder="TX" /></FormControl>
                  <FormControl isRequired>
                    <FormLabel>ZIP</FormLabel>
                    <Input value={form.zipCode} onChange={set('zipCode')} inputMode="numeric" maxLength={10} />
                  </FormControl>
                </SimpleGrid>
                <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={4}>
                  <FormControl><FormLabel>Phone</FormLabel><Input type="tel" value={form.phone} onChange={set('phone')} maxLength={30} /></FormControl>
                  <FormControl><FormLabel>Website</FormLabel><Input value={form.website} onChange={set('website')} maxLength={500} placeholder="yourlaundromat.com" /></FormControl>
                </SimpleGrid>
                <FormControl>
                  <FormLabel>Services</FormLabel>
                  <Input value={form.services} onChange={set('services')} maxLength={500}
                    placeholder="Wash & fold, Self-service, Dry cleaning, Commercial" />
                  <FormHelperText>Separate with commas.</FormHelperText>
                </FormControl>
                <FormControl>
                  <FormLabel>Short description</FormLabel>
                  <Textarea value={form.description} onChange={set('description')} maxLength={600} rows={3}
                    placeholder="What makes your laundromat great? Hours, turnaround, parking…" />
                </FormControl>
                <FormControl display="flex" alignItems="center">
                  <Switch id="offersDelivery" isChecked={form.offersDelivery} onChange={set('offersDelivery')} mr={3} />
                  <FormLabel htmlFor="offersDelivery" mb={0}>We offer pickup &amp; delivery</FormLabel>
                </FormControl>
                {form.offersDelivery && (
                  <FormControl>
                    <FormLabel>ZIP codes you deliver to</FormLabel>
                    <Input value={form.deliveryZips} onChange={set('deliveryZips')} placeholder="78664, 78665, 78681" />
                  </FormControl>
                )}

                <Heading as="h2" fontSize="lg" pt={2}>Your contact info</Heading>
                <Text fontSize="sm" color="gray.500" mt={-3}>Only used to confirm your listing. Not shown publicly.</Text>
                <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={4}>
                  <FormControl><FormLabel>Your name</FormLabel><Input value={form.contactName} onChange={set('contactName')} maxLength={255} /></FormControl>
                  <FormControl isRequired><FormLabel>Email</FormLabel><Input type="email" value={form.contactEmail} onChange={set('contactEmail')} maxLength={255} /></FormControl>
                </SimpleGrid>

                {status.error && <Alert status="error" borderRadius="md"><AlertIcon />{status.error}</Alert>}
                <Button type="submit" colorScheme="blue" size="lg" borderRadius="full" isLoading={status.sending}>
                  {claimName ? 'Claim my listing' : 'Submit my free listing'}
                </Button>
                <HStack justify="center" fontSize="xs" color="gray.500">
                  <Text>We review every listing before it goes live.</Text>
                </HStack>
              </VStack>
            </Box>

            <SoftwareUpsell claimId={claimName ? claimId : ''} />
          </VStack>
        )}
      </Container>
    </Box>
  );
}
